export default class MerideVideo {
	static _scriptPromises = new Map();

	static loadScriptOnce(src) {
		if (MerideVideo._scriptPromises.has(src)) {
			return MerideVideo._scriptPromises.get(src);
		}

		const promise = new Promise((resolve, reject) => {
			const existing = document.querySelector(`script[src="${src}"]`);
			if (existing) return resolve();

			const s = document.createElement('script');
			s.src = src;
			s.async = true;
			s.onload = () => resolve();
			s.onerror = () => reject(new Error('Failed to load ' + src));
			document.head.appendChild(s);
		});

		MerideVideo._scriptPromises.set(src, promise);
		return promise;
	}

	static triggerMerideAutoInit() {
		document.dispatchEvent(new Event('DOMContentLoaded'));
		window.dispatchEvent(new Event('load'));
	}

	/**
	 * Try to call Meride embed API explicitly so initEmbed gets embedID and embedSelector.
	 * embed.js throws "embedID or embedSelector required" when initAll runs without a valid target;
	 * when we have an explicit container (e.g. editor block) we init only this one.
	 *
	 * @param {string} embedId - Meride embed code
	 * @param {HTMLElement} target - Container element (must have id set)
	 * @returns {boolean} - True if an init function was called
	 */
	static tryExplicitInit(embedId, target) {
		const selector = target.id ? `#${target.id}` : null;
		if (!selector) return false;

		const opts = { embedID: embedId, embedSelector: selector };

		const tryCall = (fn) => {
			try {
				fn();
				return true;
			} catch (e) {
				return false;
			}
		};

		// Object form: initEmbed({ embedID, embedSelector })
		const candidates = [() => window.MerideEmbed?.initEmbed?.(opts), () => window.Meride?.embed?.init?.(opts), () => window.initMerideEmbed?.(opts)];
		for (const call of candidates) {
			if (tryCall(call)) return true;
		}

		// Positional form: initEmbed(embedID, embedSelector) or initAll(selector)
		for (const key of Object.keys(window)) {
			if (typeof window[key] !== 'object' || window[key] == null) continue;
			const initEmbed = window[key].initEmbed;
			if (typeof initEmbed !== 'function') continue;
			if (tryCall(() => initEmbed(opts))) return true;
			if (tryCall(() => initEmbed(embedId, selector))) return true;
		}
		// Some scripts expose initAll(embedSelector) to init a single container
		for (const key of Object.keys(window)) {
			if (typeof window[key] !== 'object' || window[key] == null) continue;
			const initAll = window[key].initAll;
			if (typeof initAll !== 'function') continue;
			if (tryCall(() => initAll(selector))) return true;
		}
		return false;
	}

	/**
	 * Initialize a single Meride container without triggering global init.
	 * Uses initEmbed with embedSelector to target only the specific container.
	 *
	 * @param {string} embedId - Meride embed code
	 * @param {HTMLElement} target - Container element
	 * @returns {boolean} - True if successful
	 */
	static initSingleContainer(embedId, target) {
		if (!target || !target.id) return false;

		const selector = `#${target.id}`;

		// Find Meride API object (usually named with customer ID like "mediailfoglio")
		for (const key of Object.keys(window)) {
			const obj = window[key];
			if (typeof obj !== 'object' || obj == null) continue;

			// Look for initEmbed function
			if (typeof obj.initEmbed === 'function') {
				try {
					// Meride's initEmbed expects embedID and embedSelector
					obj.initEmbed({ embedID: embedId, embedSelector: selector });
					return true;
				} catch (e) {
					// Try positional args
					try {
						obj.initEmbed(embedId, selector);
						return true;
					} catch (e2) {
						// Continue looking
					}
				}
			}
		}

		return false;
	}

	static enforceResponsiveSize(container) {
		if (!container) return;

		// Keep container aligned with the Blade wrapper sizing
		container.style.setProperty('width', '100%', 'important');
		container.style.setProperty('height', '100%', 'important');
		container.style.setProperty('max-width', '100%', 'important');
		container.style.setProperty('max-height', '100%', 'important');

		// Meride often injects wrappers with fixed px sizes: normalize everything
		const injected = container.querySelectorAll('*');
		injected.forEach((el) => {
			if (!(el instanceof HTMLElement)) return;
			el.style.setProperty('max-width', '100%', 'important');
			el.style.setProperty('max-height', '100%', 'important');
		});

		// Ensure actual media fits the box
		const video = container.querySelector('video');
		if (video) {
			video.style.setProperty('width', '100%', 'important');
			video.style.setProperty('height', '100%', 'important');
			video.style.setProperty('object-fit', 'contain', 'important');
			video.style.setProperty('display', 'block', 'important');
		}

		const iframe = container.querySelector('iframe');
		if (iframe) {
			iframe.style.setProperty('width', '100%', 'important');
			iframe.style.setProperty('height', '100%', 'important');
			iframe.style.setProperty('border', '0', 'important');
			iframe.style.setProperty('display', 'block', 'important');
		}
	}

	static async getMerideCoverUrl(container) {
		for (let i = 0; i < 30; i++) {
			// ~3s
			const posterDiv = container.querySelector('div[style*="background-image"]');
			if (posterDiv) {
				const bg = posterDiv.style.backgroundImage; // url("...")
				const m = bg.match(/url\(["']?(.*?)["']?\)/);
				if (m && m[1]) return m[1];
			}

			const video = container.querySelector('video');
			const poster = video && video.getAttribute('poster');
			if (poster) return poster;

			await new Promise((r) => setTimeout(r, 100));
		}
		return null;
	}

	static async mountMeride(customer, embedId, container = null) {
		const target = container || document.getElementById('meride-container');
		if (!target) return;

		// Reset previous render
		target.innerHTML = '';

		// Meride embed.js requires embedID or embedSelector; it uses container id as embedSelector
		if (!target.id) {
			target.id = `meride-container-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
		}

		// Ensure the container never gets locked to a fixed px size
		MerideVideo.enforceResponsiveSize(target);

		// Ensure container has the correct class for Meride (switch from pending if needed)
		target.classList.remove('meride-video-pending');
		target.classList.add('meride-video-container');

		target.setAttribute('data-customer', customer);
		target.setAttribute('data-nfs', customer);
		target.setAttribute('data-embed', embedId);
		target.setAttribute('data-responsive', 'true');
		target.setAttribute('data-autoplay', 'false');

		const embedJs = `https://media${customer}-meride-tv.akamaized.net/scripts/latest/embed.js`;
		const apiName = `media${customer}`;

		// Check if API is already available before loading script
		let merideApi = window[apiName];

		if (!merideApi || typeof merideApi.initEmbed !== 'function') {
			await MerideVideo.loadScriptOnce(embedJs);

			// Wait for Meride API object to be available with aggressive polling
			const waitForMerideAPI = () =>
				new Promise((resolve) => {
					// Immediate check
					if (window[apiName] && typeof window[apiName].initEmbed === 'function') {
						return resolve(window[apiName]);
					}

					let attempts = 0;
					const check = () => {
						attempts++;
						if (window[apiName] && typeof window[apiName].initEmbed === 'function') {
							resolve(window[apiName]);
						} else if (attempts < 50) {
							// Faster polling: 20ms intervals for first 1 second
							setTimeout(check, 20);
						} else {
							resolve(null);
						}
					};
					// Start checking immediately
					requestAnimationFrame(check);
				});

			merideApi = await waitForMerideAPI();
		}

		if (merideApi && typeof merideApi.initEmbed === 'function') {
			// Use direct API call with embedSelector to init only this container
			merideApi.initEmbed({
				embedID: embedId,
				embedSelector: `#${target.id}`,
			});
		} else {
			// Last resort fallback: trigger global init
			// This may cause errors if there are invalid containers, but it's better than nothing
			console.warn('Meride API not found, using fallback global init');
			MerideVideo.triggerMerideAutoInit();
		}

		MerideVideo.watchAndMute(target);

		const postMount = () => {
			MerideVideo.enforceResponsiveSize(target);
			MerideVideo.muteAndPause(target);
		};
		requestAnimationFrame(postMount);
		setTimeout(postMount, 100);
		setTimeout(postMount, 300);
		setTimeout(postMount, 800);
		setTimeout(postMount, 1500);
		setTimeout(postMount, 3000);
	}

	/**
	 * Set up a MutationObserver on the container to mute/pause any video or iframe
	 * injected by the Meride player, regardless of how long initialization takes.
	 * This handles AdBlock scenarios where Meride defers DOM injection beyond fixed timeouts.
	 *
	 * @param {HTMLElement} container - Container element to observe
	 * @param {number} durationMs - How long to keep the observer active (default 10s)
	 */
	static watchAndMute(container, durationMs = 10000) {
		if (!container) return;

		const disconnect = () => observer.disconnect();
		const timeoutId = setTimeout(disconnect, durationMs);

		const observer = new MutationObserver((mutations) => {
			let hasMedia = false;

			for (const m of mutations) {
				if (m.type === 'attributes' && m.attributeName === 'autoplay') {
					hasMedia = true;
					break;
				}
				if (m.type === 'childList') {
					for (const node of m.addedNodes) {
						if (node.nodeType !== 1) continue;
						if (node.tagName === 'VIDEO' || node.tagName === 'IFRAME' || node.querySelector('video, iframe')) {
							hasMedia = true;
							break;
						}
					}
				}
				if (hasMedia) break;
			}

			if (!hasMedia) return;

			MerideVideo.muteAndPause(container);

			clearTimeout(timeoutId);
			disconnect();
		});

		observer.observe(container, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['autoplay'],
		});
	}

	/**
	 * Force-mute and pause any video/iframe injected by the Meride player
	 * to prevent autoplay with sound inside the CMS detail view.
	 */
	static muteAndPause(container) {
		if (!container) return;

		const videos = container.querySelectorAll('video');
		videos.forEach((v) => {
			v.muted = true;
			v.autoplay = false;
			v.removeAttribute('autoplay');
			try {
				v.pause();
			} catch (_) {}
		});

		const iframes = container.querySelectorAll('iframe');
		iframes.forEach((iframe) => {
			try {
				const src = iframe.getAttribute('src') || '';
				if (src && !src.includes('autoplay=0') && !src.includes('autoplay=false')) {
					const separator = src.includes('?') ? '&' : '?';
					iframe.setAttribute('src', src + separator + 'autoplay=0&muted=1');
				}
			} catch (_) {}
		});
	}

	/**
	 * Create HTML for a Meride popup container.
	 * @param {string} customer - Meride customer ID
	 * @param {string} code - Meride embed code
	 * @returns {{ html: string, containerId: string }}
	 */
	static createPopupContainer(customer, code) {
		const containerId = 'mfp-meride-' + Date.now().toString(36);
		const html = `<div class="mfp-meride-container mfp-video-container" style="position: relative; width: 100%; max-width: 900px; margin: 0 auto;">
            <div id="${containerId}" class="meride-video-container"
                data-customer="${customer}" data-nfs="${customer}"
                data-embed="${code}" data-responsive="true"
                data-autoplay="false"
                style="width: 100%; min-height: min(50vh, 360px);">
            </div>
        </div>`;
		return { html, containerId };
	}

	/**
	 * Initialize Meride player inside a popup container.
	 * @param {string} containerId - ID of the container element
	 * @param {string} customer - Meride customer ID
	 * @param {string} code - Meride embed code
	 */
	static initPopupPlayer(containerId, customer, code) {
		setTimeout(() => {
			const el = document.getElementById(containerId);
			if (!el || !customer || !code) return;

			const embedJs = `https://media${customer}-meride-tv.akamaized.net/scripts/latest/embed.js`;
			const existing = document.querySelector(`script[src="${embedJs}"]`);

			const forceFullWidth = () => {
				if (!el) return;
				el.querySelectorAll('*').forEach((child) => {
					if (!(child instanceof HTMLElement)) return;
					child.style.setProperty('max-width', '100%', 'important');
					child.style.setProperty('background', 'transparent', 'important');
				});
				const v = el.querySelector('video');
				if (v) {
					v.style.setProperty('width', '100%', 'important');
					v.style.setProperty('height', 'auto', 'important');
					v.style.setProperty('max-height', '85vh', 'important');
					v.style.setProperty('display', 'block', 'important');
				}
				const ifr = el.querySelector('iframe');
				if (ifr) {
					ifr.style.setProperty('width', '100%', 'important');
					ifr.style.setProperty('height', 'auto', 'important');
					ifr.style.setProperty('min-height', '50vh', 'important');
					ifr.style.setProperty('max-height', '85vh', 'important');
					ifr.style.setProperty('border', '0', 'important');
				}
			};

			const ready = () => {
				MerideVideo.triggerMerideAutoInit();
				setTimeout(forceFullWidth, 200);
				setTimeout(forceFullWidth, 600);
				setTimeout(forceFullWidth, 1500);
			};

			if (existing) {
				ready();
			} else {
				const s = document.createElement('script');
				s.src = embedJs;
				s.async = true;
				s.onload = ready;
				document.head.appendChild(s);
			}
		}, 100);
	}

	/**
	 * Cleanup all Meride popup players (stop videos, remove containers).
	 */
	static cleanupPopupPlayers() {
		document.querySelectorAll('.mfp-meride-container').forEach((container) => {
			container.querySelectorAll('video').forEach((v) => {
				v.pause();
				v.muted = true;
				v.src = '';
				v.load();
			});
			container.querySelectorAll('iframe').forEach((ifr) => {
				ifr.src = 'about:blank';
			});
			container.remove();
		});

		document.querySelectorAll('[id^="mfp-meride-"]').forEach((el) => {
			el.querySelectorAll('video').forEach((v) => {
				v.pause();
				v.muted = true;
				v.src = '';
				v.load();
			});
			const parent = el.closest('.mfp-meride-container');
			if (parent) parent.remove();
		});
	}
}
